/**
 * OverlayOrchestrator
 *
 * Global overlay stack manager. Replaces per-component useState(isOpen)
 * with a centralized stack that:
 * - tracks every open drawer/modal as an OverlayEntry
 * - assigns z-index tiers automatically
 * - carries OriginEnvelope so every close resolves deterministically
 * - emits lifecycle events for highlight/scroll restoration
 * - supports idempotent open (double-click safe)
 * - tracks overlay lifecycle states (opening → open → closing → closed)
 * - provides parent lookup for nested overlays
 *
 * This is a pure state module (no React dependency). The React bindings
 * live in OverlayOrchestratorContext.tsx and useOverlay.ts.
 *
 * @module lib/navigation/overlayOrchestrator
 */

import type {
    SurfaceId,
    OverlayKind,
    OverlayEntry,
    OverlayLifecycle,
    OriginEnvelope,
    OverlayEvent,
    OverlayEventHandler,
    NavigationContext,
    ReturnPolicy,
} from './types';
import { surfaceKey } from './types';
import { resolveCanonicalHost } from './canonicalHostRegistry';
import { emitTelemetry } from './telemetry';
import { requireRegistration } from './migrationBoundary';

// ── Z-index tiers ─────────────────────────────────────────────────────────
const Z_BASE_DRAWER = 45;
const Z_BASE_MODAL = 50;
const Z_INCREMENT = 5;

// ── State ─────────────────────────────────────────────────────────────────
let stack: OverlayEntry[] = [];
const listeners = new Set<OverlayEventHandler>();
let idCounter = 0;

function generateInstanceId(): string {
    idCounter += 1;
    return `overlay-${Date.now()}-${idCounter}`;
}

function emit(event: OverlayEvent): void {
    listeners.forEach((fn) => {
        try { fn(event); } catch { /* listener error — don't break the stack */ }
    });
}

function computeZIndex(kind: OverlayKind): number {
    const base = kind === 'modal' ? Z_BASE_MODAL : Z_BASE_DRAWER;
    const sameKindCount = stack.filter((e) => e.overlayKind === kind).length;
    return base + sameKindCount * Z_INCREMENT;
}

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Open an overlay and push it onto the stack.
 *
 * IDEMPOTENT: If a surface with the same surfaceKey is already on the
 * stack in 'opening' or 'open' state, returns the existing entry
 * (no-op). This prevents double-open from fast double-clicks.
 *
 * Automatically populates canonicalFallback on the origin envelope
 * from the CanonicalHostRegistry.
 */
export function openOverlay(params: {
    surface: SurfaceId;
    overlayKind: OverlayKind;
    origin: OriginEnvelope;
    props?: Record<string, unknown>;
}): OverlayEntry {
    // ── Idempotent open guard ──────────────────────────────────────────
    const key = surfaceKey(params.surface);

    // Phase C: Ensure every surface is registered in the migration boundary
    requireRegistration(params.surface.name);

    const existing = stack.find(
        (e) => surfaceKey(e.surface) === key &&
            (e.lifecycle === 'opening' || e.lifecycle === 'open'),
    );
    if (existing) {
        emitTelemetry('idempotent-open-blocked', {
            surface: params.surface.name,
            existingInstanceId: existing.instanceId,
        });
        return existing;
    }

    // ── Auto-populate canonical fallback ───────────────────────────────
    let canonicalFallback = params.origin.canonicalFallback;
    if (!canonicalFallback) {
        const host = resolveCanonicalHost(params.surface.kind, params.surface.name);
        if (host) {
            canonicalFallback = {
                route: host.hostRoute,
                params: host.hostParams,
            };
        }
    }

    // ── Set return policy default ──────────────────────────────────────
    let returnPolicy = params.origin.returnPolicy;
    let parentSessionId = params.origin.parentSessionId;
    if (!returnPolicy) {
        const parentOverlay = stack.length > 0 ? stack[stack.length - 1] : null;
        if (parentOverlay) {
            returnPolicy = 'close-to-parent';
            parentSessionId = parentOverlay.instanceId;
        } else {
            returnPolicy = 'restore-origin';
        }
    }

    const origin: OriginEnvelope = {
        ...params.origin,
        canonicalFallback,
        returnPolicy,
        parentSessionId,
    };

    const zIndex = computeZIndex(params.overlayKind);

    const entry: OverlayEntry = {
        instanceId: generateInstanceId(),
        surface: params.surface,
        overlayKind: params.overlayKind,
        origin,
        zIndex,
        backdropZIndex: zIndex - 5,
        props: params.props ?? {},
        openedAt: Date.now(),
        lifecycle: 'opening',
    };
    stack = [...stack, entry];
    emit({ type: 'opened', entry });
    emitTelemetry('overlay-opened', {
        surface: params.surface.name,
        kind: params.overlayKind,
        instanceId: entry.instanceId,
        returnPolicy: origin.returnPolicy,
        stackDepth: stack.length,
    });
    return entry;
}

/**
 * Transition an overlay's lifecycle: opening → open, or closing → closed.
 * Called by the React component after animation completes.
 */
export function markLifecycle(instanceId: string, lifecycle: OverlayLifecycle): void {
    const entry = stack.find((e) => e.instanceId === instanceId);
    if (!entry) return;
    entry.lifecycle = lifecycle;
    emit({ type: 'lifecycle', instanceId, lifecycle });

    // If marked closed, remove from stack
    if (lifecycle === 'closed') {
        stack = stack.filter((e) => e.instanceId !== instanceId);
    }
}

/**
 * Close the topmost overlay, or a specific overlay by instanceId.
 *
 * Transitions the overlay to 'closing'. The actual removal from the
 * stack happens when the React layer calls `markLifecycle('closed')`
 * after the exit animation completes (see `useOrchestratedOverlay`).
 *
 * `opts.immediate = true` removes the entry synchronously from the
 * stack — only for non-animated contexts (route-change cleanup, tests,
 * imperative cancellations). Default behavior keeps the orchestrator
 * stack in sync with the React render tree.
 *
 * Returns the closed entry (with optional returnPayload attached) or null.
 */
export function closeOverlay(
    instanceIdOrTop?: string,
    returnPayload?: Record<string, unknown>,
    opts?: { immediate?: boolean },
): OverlayEntry | null {
    if (stack.length === 0) return null;

    let closedEntry: OverlayEntry | undefined;

    if (instanceIdOrTop) {
        closedEntry = stack.find((e) => e.instanceId === instanceIdOrTop);
        if (!closedEntry) return null;
    } else {
        closedEntry = stack[stack.length - 1];
    }

    if (returnPayload) {
        closedEntry.origin.returnPayload = returnPayload;
    }

    if (opts?.immediate) {
        // Immediate removal — used by route-change cleanup and tests.
        closedEntry.lifecycle = 'closed';
        stack = stack.filter((e) => e.instanceId !== closedEntry!.instanceId);
    } else {
        // Default: animation-safe close. Entry remains on stack with
        // lifecycle='closing' so the React tree can run its exit animation
        // and emit onExitComplete → markLifecycle('closed') for removal.
        closedEntry.lifecycle = 'closing';
        emit({ type: 'lifecycle', instanceId: closedEntry.instanceId, lifecycle: 'closing' });
    }

    emit({
        type: 'closed',
        instanceId: closedEntry.instanceId,
        returnPayload,
    });

    emitTelemetry('overlay-closed', {
        surface: closedEntry.surface.name,
        instanceId: closedEntry.instanceId,
        returnPolicy: closedEntry.origin.returnPolicy,
        immediate: !!opts?.immediate,
        stackDepth: stack.length,
    });

    return closedEntry;
}

/**
 * Replace an overlay on the stack without close animation.
 * Useful for swapping entity context (e.g. "next milestone" nav within drawer).
 *
 * Telemetry: emits 'overlay-close-sequenced' for old + 'overlay-opened' for new.
 * The old overlay's close event does NOT carry returnPayload (no restoration).
 */
export function replaceOverlay(
    instanceId: string,
    params: {
        surface: SurfaceId;
        origin: OriginEnvelope;
        props?: Record<string, unknown>;
    },
): OverlayEntry | null {
    const idx = stack.findIndex((e) => e.instanceId === instanceId);
    if (idx === -1) return null;

    const old = stack[idx];
    const replacement: OverlayEntry = {
        instanceId: generateInstanceId(),
        surface: params.surface,
        overlayKind: old.overlayKind,
        origin: params.origin,
        zIndex: old.zIndex,
        backdropZIndex: old.backdropZIndex,
        props: params.props ?? {},
        openedAt: Date.now(),
        lifecycle: 'open',
    };

    stack = [...stack.slice(0, idx), replacement, ...stack.slice(idx + 1)];
    emit({ type: 'closed', instanceId: old.instanceId });
    emit({ type: 'opened', entry: replacement });

    emitTelemetry('overlay-close-sequenced', {
        replacedSurface: old.surface.name,
        replacedInstanceId: old.instanceId,
        newSurface: params.surface.name,
        newInstanceId: replacement.instanceId,
        stackDepth: stack.length,
    });

    return replacement;
}

/**
 * Close all overlays above (and including) a given instanceId.
 * Closes top-down (children first) so restoration events fire in the
 * correct order and intermediate overlays don't flash focus.
 *
 * Emits 'nested-close-cascade' telemetry. Close events for intermediate
 * overlays carry `cascade: true` so listeners can skip restoration.
 */
export function closeAllAbove(instanceId: string): OverlayEntry[] {
    const idx = stack.findIndex((e) => e.instanceId === instanceId);
    if (idx === -1) return [];

    // Slice and reverse: close children (top) before parent (bottom)
    const removed = stack.slice(idx);
    stack = stack.slice(0, idx);

    const reversed = [...removed].reverse();
    reversed.forEach((entry, i) => {
        entry.lifecycle = 'closed';
        const isRoot = i === reversed.length - 1; // the entry that was requested
        emit({
            type: 'closed',
            instanceId: entry.instanceId,
            cascade: !isRoot,
        });
    });

    emitTelemetry('nested-close-cascade', {
        rootInstanceId: instanceId,
        closedCount: removed.length,
        surfaces: removed.map((e) => e.surface.name),
    });

    return removed;
}

/**
 * Close every overlay in the stack. Closes top-down.
 */
export function closeAll(): OverlayEntry[] {
    const removed = [...stack];
    stack = [];

    // Close top-down
    const reversed = [...removed].reverse();
    reversed.forEach((entry, i) => {
        entry.lifecycle = 'closed';
        const isLast = i === reversed.length - 1;
        emit({
            type: 'closed',
            instanceId: entry.instanceId,
            cascade: !isLast,
        });
    });

    if (removed.length > 1) {
        emitTelemetry('nested-close-cascade', {
            rootInstanceId: 'close-all',
            closedCount: removed.length,
            surfaces: removed.map((e) => e.surface.name),
        });
    }

    return removed;
}

/** Get the current stack (read-only snapshot). */
export function getStack(): readonly OverlayEntry[] {
    return stack;
}

/** Get the topmost overlay entry, or null. */
export function getTopOverlay(): OverlayEntry | null {
    return stack.length > 0 ? stack[stack.length - 1] : null;
}

/** Get the parent overlay of a given entry (the entry below it in the stack). */
export function getParentOverlay(instanceId: string): OverlayEntry | null {
    const idx = stack.findIndex((e) => e.instanceId === instanceId);
    return idx > 0 ? stack[idx - 1] : null;
}

/** Check whether a surface is currently open in the stack. */
export function isOpen(surface: SurfaceId): boolean {
    const key = surfaceKey(surface);
    return stack.some((e) => surfaceKey(e.surface) === key);
}

/** Find an overlay entry by surface name (ignores entityId). */
export function findBySurfaceName(name: string): OverlayEntry | undefined {
    return stack.find((e) => e.surface.name === name);
}

/** Find an overlay entry by its instanceId */
export function findByInstanceId(instanceId: string): OverlayEntry | undefined {
    return stack.find((e) => e.instanceId === instanceId);
}

/** Subscribe to overlay lifecycle events. Returns unsubscribe function. */
export function subscribe(handler: OverlayEventHandler): () => void {
    listeners.add(handler);
    return () => { listeners.delete(handler); };
}

/**
 * Determine the effective return policy for a closing overlay.
 * Considers whether the parent is still on the stack and falls back
 * to canonical host when needed.
 */
export function resolveReturnPolicy(entry: OverlayEntry): ReturnPolicy {
    const preferred = entry.origin.returnPolicy ?? 'restore-origin';

    if (preferred === 'close-to-parent') {
        // Verify parent is still on stack
        const parentId = entry.origin.parentSessionId;
        if (parentId && stack.some((e) => e.instanceId === parentId)) {
            return 'close-to-parent';
        }
        // Parent gone — fall through
    }

    if (preferred === 'restore-origin') {
        // Check if host page could receive focus
        // (We can't check if a React component is mounted from here,
        //  so we trust the envelope. The React layer handles fallback.)
        return 'restore-origin';
    }

    return entry.origin.canonicalFallback ? 'redirect-canonical' : 'restore-origin';
}

/**
 * Build an OriginEnvelope capturing the current surface context.
 * Convenience helper used by hooks when opening child overlays.
 */
export function buildOriginEnvelope(
    originSurface: SurfaceId,
    context: NavigationContext,
    triggerElementId?: string,
    returnPolicy?: ReturnPolicy,
): OriginEnvelope {
    return {
        origin: originSurface,
        context,
        triggerElementId,
        returnPolicy,
    };
}
